import React, { useState, useRef, useEffect } from "react";
import axios from "axios";

const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;

// Patient document types the mobile uploader can tag the file with.
const DOCUMENT_TYPES = [
  { value: "insurance_card", label: "Insurance Card / Policy" },
  { value: "government_id", label: "Government ID (Aadhaar / PAN / etc.)" },
  { value: "hospital_document", label: "Hospital / Referral Document" },
  { value: "other", label: "Other" },
];

// This page is opened on the patient's own phone via a QR code, so it is NOT
// logged in. We use a bare axios instance (no app-wide interceptors). The upload
// is authorised only by the signed, single-use token in the QR (`t`); the backend
// takes the request_id the facility screen polls against from that token.
// `org`/`app` are non-secret and only route the request to the tenant DB.
const rawAxios = axios.create();

const EXPIRED_MSG =
  "This QR code has expired or was already used. Please ask the staff to show a new code.";

function MobileScan() {
  // mode=photo: the patient-photo QR from IPD registration (one JPG/PNG, no OCR).
  const [isPhoto] = useState(
    () => new URLSearchParams(window.location.search).get("mode") === "photo"
  );
  const [link, setLink] = useState(null); // { token, org, app }
  const [documentType, setDocumentType] = useState("");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [status, setStatus] = useState("idle"); // "idle" | "uploading" | "success" | "error"
  const [errorMsg, setErrorMsg] = useState("");
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("t");
    const org = params.get("org");
    const app = params.get("app");
    if (token && org && app) {
      setLink({ token, org, app });
    } else {
      setErrorMsg("Invalid link. Please scan the QR code shown by the staff again.");
      setStatus("error");
    }
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileChange = (e) => {
    const selected = e.target.files && e.target.files[0];
    if (!selected) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(selected);
    setPreviewUrl(
      selected.type?.startsWith("image/") ? URL.createObjectURL(selected) : null
    );
    setErrorMsg("");
  };

  const uploadDocument = async () => {
    if (!link) return;
    if (!isPhoto && !documentType) {
      setErrorMsg("Please select a document type.");
      return;
    }
    if (!file) {
      setErrorMsg(
        isPhoto
          ? "Please choose or take a photo to upload."
          : "Please choose or capture a document to upload."
      );
      return;
    }

    setStatus("uploading");
    setErrorMsg("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (!isPhoto) formData.append("document_type", documentType);

      const res = await rawAxios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}${isPhoto ? "ipd-qr-photo-upload/" : "ipd-qr-document-upload/"}`,
        formData,
        {
          headers: {
            "Org-Code": link.org,
            "App-Mode": link.app,
            "QR-Token": link.token,
          },
        }
      );

      if (res?.data?.status === "Success") {
        setStatus("success");
      } else {
        setErrorMsg(
          res?.data?.message || "Upload did not complete. Please try again."
        );
        setStatus("error");
      }
    } catch (err) {
      console.error("[MobileScan] upload error:", err);
      const httpStatus = err?.response?.status;
      if (httpStatus === 403) {
        setErrorMsg(EXPIRED_MSG);
        setStatus("error");
        return;
      }
      const serverMsg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" ? err.response.data : null);
      setErrorMsg(
        `Upload failed${httpStatus ? ` (${httpStatus})` : ""}${
          serverMsg ? ": " + serverMsg : ". Please try again."
        }`
      );
      setStatus("error");
    }
  };

  const removeFile = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    setErrorMsg("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  const resetForRetry = () => {
    setStatus("idle");
    setErrorMsg("");
  };

  const styles = {
    container: {
      minHeight: "100vh",
      backgroundColor: "#f0f9fb",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "24px 16px",
      fontFamily: "'Roboto', sans-serif",
    },
    header: {
      color: "#177f97",
      fontSize: 22,
      fontWeight: 700,
      marginBottom: 8,
      textAlign: "center",
    },
    subtext: {
      color: "#666",
      fontSize: 14,
      textAlign: "center",
      marginBottom: 24,
    },
    card: {
      width: "100%",
      maxWidth: 400,
      backgroundColor: "#fff",
      borderRadius: 16,
      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
      padding: 24,
    },
    label: {
      display: "block",
      textAlign: "left",
      color: "#333",
      fontSize: 14,
      fontWeight: 600,
      marginBottom: 6,
    },
    select: {
      width: "100%",
      padding: "12px",
      borderRadius: 10,
      border: "1.5px solid #cdd9dc",
      fontSize: 15,
      color: "#333",
      backgroundColor: "#fff",
      marginBottom: 20,
    },
    dropzone: {
      border: "2px dashed #177f97",
      borderRadius: 10,
      padding: "20px",
      textAlign: "center",
      cursor: "pointer",
      backgroundColor: "#f0fbfc",
    },
    primaryBtn: {
      backgroundColor: "#177f97",
      color: "#fff",
      border: "none",
      borderRadius: 10,
      padding: "14px 24px",
      fontWeight: 700,
      fontSize: 16,
      cursor: "pointer",
      width: "100%",
      marginTop: 20,
    },
    secondaryBtn: {
      backgroundColor: "#fff",
      color: "#177f97",
      border: "2px solid #177f97",
      borderRadius: 10,
      padding: "12px 24px",
      fontWeight: 600,
      fontSize: 15,
      cursor: "pointer",
      width: "100%",
      marginTop: 10,
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        {isPhoto ? "Upload Patient Photo" : "Upload Patient Document"}
      </div>
      <div style={styles.subtext}>
        {isPhoto
          ? "Take or choose a clear photo of the patient's face."
          : "Select the type of document and upload a clear photo or file."}
      </div>

      <div style={styles.card}>
        {/* Form — pick document type (documents only) and upload */}
        {(status === "idle" || status === "uploading") && link && (
          <>
            {!isPhoto && (
              <>
                <label style={styles.label} htmlFor="doc-type">
                  Document type
                </label>
                <select
                  id="doc-type"
                  style={styles.select}
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                  disabled={status === "uploading"}
                >
                  <option value="">Select document type…</option>
                  {DOCUMENT_TYPES.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </>
            )}

            <label style={styles.label}>{isPhoto ? "Photo" : "Document"}</label>
            <div style={{ position: "relative" }}>
              <div
                style={styles.dropzone}
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
              >
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt={isPhoto ? "Photo preview" : "Document preview"}
                    style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 6 }}
                  />
                ) : file ? (
                  <div style={{ color: "#177f97" }}>
                    <p style={{ marginTop: 8, wordBreak: "break-all" }}>{file.name}</p>
                  </div>
                ) : (
                  <div style={{ color: "#177f97" }}>
                    <p style={{ marginTop: 8, fontWeight: 600 }}>
                      Tap to choose a file
                    </p>
                    <p style={{ fontSize: 12, color: "#888" }}>
                      {isPhoto ? "JPG or PNG, up to 5 MB" : "JPG, PNG or PDF"}
                    </p>
                  </div>
                )}
              </div>
              {file && status !== "uploading" && (
                <button
                  type="button"
                  aria-label="Remove document"
                  title="Remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile();
                  }}
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    border: "none",
                    backgroundColor: "rgba(0,0,0,0.55)",
                    color: "#fff",
                    fontSize: 15,
                    lineHeight: 1,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept={isPhoto ? "image/jpeg,image/png" : "image/*,application/pdf"}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />

            <button
              type="button"
              style={styles.secondaryBtn}
              disabled={status === "uploading"}
              onClick={() => cameraInputRef.current && cameraInputRef.current.click()}
            >
              Take Photo with Camera
            </button>

            {errorMsg && (
              <p style={{ color: "#d32f2f", fontSize: 13, marginTop: 12 }}>
                {errorMsg}
              </p>
            )}

            <button
              type="button"
              style={{
                ...styles.primaryBtn,
                cursor: status === "uploading" ? "not-allowed" : "pointer",
                opacity: status === "uploading" ? 0.7 : 1,
              }}
              disabled={status === "uploading"}
              onClick={uploadDocument}
            >
              {status === "uploading"
                ? "Uploading…"
                : isPhoto
                  ? "Upload Photo"
                  : "Upload Document"}
            </button>
          </>
        )}

        {/* Success */}
        {status === "success" && (
          <div style={{ textAlign: "center" }}>
            <p style={{ color: "#177f97", fontWeight: 700, fontSize: 18, marginBottom: 8 }}>
              {isPhoto ? "Photo Uploaded!" : "Document Uploaded!"}
            </p>
            <p style={{ color: "#666", fontSize: 14 }}>
              You may now close this tab.
            </p>
          </div>
        )}

        {/* Error (fatal — no session, or after a failed upload) */}
        {status === "error" && (
          <div style={{ textAlign: "center" }}>
            <p style={{ color: "#d32f2f", fontWeight: 600, fontSize: 15 }}>
              {errorMsg || "Something went wrong."}
            </p>
            {link && errorMsg !== EXPIRED_MSG && (
              <button type="button" style={styles.secondaryBtn} onClick={resetForRetry}>
                Try Again
              </button>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

export default MobileScan;
